import type { ReactNode } from 'react';

import Box from '@mui/material/Box';
import Drawer from '@mui/material/Drawer';
import Toolbar from '@mui/material/Toolbar';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import CancelIcon from '@mui/icons-material/Cancel';
import type { SxProps, Theme } from '@mui/material';

import { checkIfIsArray } from '@e-socna/utils';
import { EMPTY_ARRAY, EMPTY_OBJECT } from '@e-socna/empty';

import { useUiContext } from '../contexts/UiContext/hooks/useUiContext.hook';
import { useDialogAutoFocus } from '../hooks/useDialogAutoFocus.hook';

interface Props {
  readonly children: ReactNode;
  readonly isOpen: boolean;
  readonly onDrawerClose: () => void;
  readonly sx?: SxProps<Theme>;
  readonly title?: string;
  // Under the title (the title and code of the entity, ...).
  readonly subtitle?: ReactNode;
}

export const RIGHT_DRAWER_MAX_WIDTH = 1200;
export const RIGHT_DRAWER_MIN_WIDTH = 800;

export const RightDrawer = ({
  children,
  isOpen,
  onDrawerClose: handleDrawerClose,
  subtitle,
  sx,
  title,
}: Readonly<Props>) => {
  const {
    ui: { isDataGridVisible },
  } = useUiContext();

  const dialogAutoFocusSlotProps = useDialogAutoFocus();

  return (
    <Drawer
      ModalProps={{ disableEnforceFocus: true }}
      anchor="right"
      onClose={handleDrawerClose}
      open={isOpen}
      slotProps={dialogAutoFocusSlotProps}
      sx={[
        isDataGridVisible ? { maxHeight: '100vh' } : EMPTY_OBJECT,
        ...(!sx ? EMPTY_ARRAY : checkIfIsArray(sx) ? sx : [sx]),
      ]}
    >
      <Toolbar />
      {!!title && (
        <Box sx={{ alignItems: 'flex-start', display: 'flex', mx: 3, mt: 3 }}>
          <Box sx={{ flexGrow: 1 }}>
            <Typography variant="h5">{title}</Typography>
            {subtitle}
          </Box>
          <Tooltip title="Fermer le volet">
            <IconButton
              aria-label="Fermer le volet"
              onClick={handleDrawerClose}
            >
              <CancelIcon />
            </IconButton>
          </Tooltip>
        </Box>
      )}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          overflow: 'auto',
          p: 3,
          maxWidth: `min(${RIGHT_DRAWER_MAX_WIDTH.toFixed(2)}px, 100vw)`,
          minWidth: `min(${RIGHT_DRAWER_MIN_WIDTH.toFixed(2)}px, 100vw)`,
        }}
      >
        {children}
      </Box>
    </Drawer>
  );
};
